import type { Engine } from "../../../engine/Engine";
import { SugarModel } from "../../../loader/SugarModel";
import type { Room } from "../../../shape/Scene";
import type { PlanarGraphController } from "../../../shape/segment/PlanarGraphController";
import { PLANAR_GRAPH_CHANGED_EVENT } from "../../../shape/segment/segmentPointerEvents";
import { resolveMeasurement } from "./MeasurementAnchorResolver";
import { resolveAutoClearance } from "./autoClearanceRays";
import type { MeasurementPreview } from "./MeasurementSnapResolver";
import {
  MEASUREMENTS_CHANGED_EVENT,
  type MeasurementStore,
} from "./MeasurementStore";
import type { ResolvedMeasurement, ProductMeasurement } from "./MeasurementTypes";
import type { MeasurementApi } from "./MeasurementApi";
import { LENGTH_UNIT_CHANGED_EVENT } from "../../../core/units/LengthUnit";
import type { DesignerSelection } from "../object/DesignerSelection";
import { MeasurementDesignerObject } from "../object/MeasurementDesignerObject";
import type { DesignerMeterHost } from "./DesignerMeterHost";
import type { DesignerMeterItem } from "./DesignerMeterLabel";

export const MEASUREMENT_PREVIEW_EVENT = "measurement-preview-changed";

const Y_MEASURE = 0.02;

export class MeasurementRenderer {
  private _previewSnap: MeasurementPreview | null = null;

  constructor(
    private readonly _engine: Engine,
    private readonly _store: MeasurementStore,
    private readonly _segments: PlanarGraphController,
    private readonly _api: MeasurementApi,
    private readonly _selection: DesignerSelection,
    private readonly _scene: Room,
    private readonly _meters: DesignerMeterHost,
  ) {
    _engine.addEventListener(MEASUREMENTS_CHANGED_EVENT, this._refresh);
    _engine.addEventListener(MEASUREMENT_PREVIEW_EVENT, this._onPreview);
    _engine.addEventListener(PLANAR_GRAPH_CHANGED_EVENT, this._refresh);
    _engine.addEventListener("dragging-end", this._refresh);
    _engine.addEventListener("select-product", this._refresh);
    _engine.addEventListener("product-transform-changed", this._refresh);
    window.addEventListener(LENGTH_UNIT_CHANGED_EVENT, this._refresh);
    this._refresh();
  }

  dispose(): void {
    this._engine.removeEventListener(MEASUREMENTS_CHANGED_EVENT, this._refresh);
    this._engine.removeEventListener(MEASUREMENT_PREVIEW_EVENT, this._onPreview);
    this._engine.removeEventListener(PLANAR_GRAPH_CHANGED_EVENT, this._refresh);
    this._engine.removeEventListener("dragging-end", this._refresh);
    this._engine.removeEventListener("select-product", this._refresh);
    this._engine.removeEventListener("product-transform-changed", this._refresh);
    window.removeEventListener(LENGTH_UNIT_CHANGED_EVENT, this._refresh);
    this._meters.setLayer("measurement", []);
  }

  private _onPreview = (event: Event): void => {
    this._previewSnap =
      (event as CustomEvent<MeasurementPreview | null>).detail ?? null;
    this._refresh();
  };

  private _refresh = (): void => {
    const models = this._sceneModels();
    const items: DesignerMeterItem[] = [];
    for (const item of this._store.items) {
      const resolvedList =
        item.mode === "auto"
          ? this._resolveAuto(item, models)
          : resolveMeasurement(item, this._segments);
      const resolvedItems = Array.isArray(resolvedList)
        ? resolvedList
        : resolvedList
          ? [resolvedList]
          : [];
      resolvedItems.forEach((resolved, index) => {
        if (!isVisibleDistance(resolved.distance)) return;
        const key =
          item.mode === "auto" ? `${item.id}:${index}` : item.id;
        items.push(
          toMeasurementItem(key, resolved, {
            selected: item.id === this._store.selectedId,
            onSelect: () =>
              this._selection.select(
                new MeasurementDesignerObject(item.id, this._store, this._api),
              ),
          }),
        );
      });
    }
    if (this._previewSnap && isVisibleDistance(this._previewSnap.distance)) {
      items.push(
        toMeasurementItem("preview", this._previewSnap, { preview: true }),
      );
    }
    this._meters.setLayer("measurement", items);
  };

  private _sceneModels(): SugarModel[] {
    return (this._scene.models ?? []).filter(
      (model): model is SugarModel => model instanceof SugarModel,
    );
  }

  private _resolveAuto(
    item: ProductMeasurement,
    models: SugarModel[],
  ): ResolvedMeasurement[] {
    const model = models.find((entry) => entry.uuid === item.source.modelUuid);
    if (!model?.loaded) return [];
    const ready = models.filter((entry) => entry.loaded);
    return resolveAutoClearance(item, model, this._segments, ready);
  }
}

function toMeasurementItem(
  key: string,
  measurement: Pick<ResolvedMeasurement, "start" | "end" | "distance">,
  extra: Pick<DesignerMeterItem, "selected" | "preview" | "onSelect">,
): DesignerMeterItem {
  return {
    key,
    start: { x: measurement.start.x, y: Y_MEASURE, z: measurement.start.z },
    end: { x: measurement.end.x, y: Y_MEASURE, z: measurement.end.z },
    length: measurement.distance,
    mode: "readonly",
    flush: true,
    ...extra,
  };
}

function isVisibleDistance(distance: number): boolean {
  return Math.round(distance * 1000) > 0;
}
